import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DocumentIntelligenceModel } from '../../../shared/models/document-intelligence-model.model';
import { DocumentIntelligenceService } from '../../../shared/services/document-intelligence.service';
import { ModalBaseComponent } from '../../../shared/components/base/modal-base/modal-base.component';

@Component({
    selector: 'app-model',
    templateUrl: './model.component.html',
    styleUrls: ['./model.component.scss']
})
export class ModelComponent extends ModalBaseComponent implements OnInit {
    readonly NO_FORMAT_CONVERTER = 'none';
    readonly MAX_LENGTH = 200;

    readonly PARBLE_PROVIDER = 'parble';

    providers: string[] = [];
    formatConverters: string[] = [];
    defaultFormatConverter: string = this.NO_FORMAT_CONVERTER;
    isEditMode: boolean = false;
    isViewMode: boolean = false;

    /** Whether the mapping specification currently in the textarea is unusable. */
    hasMappingSpecificationError: boolean = false;

    /**
     * A mapping specification names header fields and tables in the provider's own vocabulary, so it
     * only means anything for Parble. The field is hidden for anything else rather than offered and
     * then ignored.
     */
    get supportsMappingSpecification(): boolean {
        return (this.currentObject?.Provider || '').toLowerCase() === this.PARBLE_PROVIDER;
    }

    constructor(
        protected activeModal: NgbActiveModal,
        protected modalService: NgbModal,
        private documentIntelligenceService: DocumentIntelligenceService
    ) {
        super(activeModal)
    }

    ngOnInit(): void {
        this.initializeObject();
        this.loadProviders();
    }

    initializeObject(): void {
        if (this.currentObject == null) {
            this.currentObject = new DocumentIntelligenceModel();
        }

        this.defaultFormatConverter = this.currentObject.DefaultFormatConverter ?? this.NO_FORMAT_CONVERTER;
        this.onMappingSpecificationChanged();

        // Opening the dialog only loads the converters for the provider already on the model; it must
        // not go through onProviderChange, which clears the specification for a non-Parble provider
        // and would so drop a specification the model was saved with before the user touched anything.
        if (this.currentObject.Provider) {
            this.loadFormatConverters();
        }

        super.initializeObject();
    }

    loadProviders(): void {
        this.documentIntelligenceService.getProviders((providers: string[]) => {
            this.providers = providers;
        });
    }

    loadFormatConverters(): void {
        this.documentIntelligenceService.getFormatConverters(this.currentObject.Provider, (converters: string[]) => {
            this.formatConverters = converters;
            if (!this.formatConverters.find(x => x == this.defaultFormatConverter)) {
                this.defaultFormatConverter = this.NO_FORMAT_CONVERTER;
            }
        });
    }
    
    onProviderChange(provider: string): void {
        // Moving off Parble drops the specification: the field is hidden from here on, so leaving it
        // in place would keep posting a value the user can no longer see or correct.
        if (!this.supportsMappingSpecification) {
            this.currentObject.MappingSpecification = null;
            this.hasMappingSpecificationError = false;
        }

        this.loadFormatConverters();
    }

    onMappingSpecificationChanged(): void {
        // Only a visible field may block the save. The textarea is hidden for anything but Parble, and
        // a specification stored against another provider is kept, so without this guard a model could
        // be left unsavable over text the user has no way to see or correct.
        this.hasMappingSpecificationError = this.supportsMappingSpecification
            && !this.isValidMappingSpecification(this.currentObject.MappingSpecification);
    }

    /**
     * Reset restores the textarea through the form, which never runs the blur handler that maintains
     * the error flag - without this the flag stays raised and keeps Save disabled on a clean form.
     */
    resetAdditionalFields(): void {
        super.resetAdditionalFields();
        this.onMappingSpecificationChanged();
    }

    /**
     * A shape check only - that the text is well-formed JSON and an object.
     *
     * The schema itself (the types, the field and table names, no nesting under an array) is left to
     * the API, which owns the one compiler the analysis actually runs. Repeating those rules here would
     * only give them somewhere to drift apart.
     */
    private isValidMappingSpecification(value: string): boolean {
        if (value == null || value.trim() === '') {
            return true;
        }

        try {
            const parsed = JSON.parse(value);
            return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed);
        } catch {
            return false;
        }
    }

    onSubmit(form: NgForm): void {
        if (!form.valid || this.hasMappingSpecificationError) {
            return;
        }

        this.currentObject.DefaultFormatConverter = this.defaultFormatConverter == this.NO_FORMAT_CONVERTER
            ? null
            : this.defaultFormatConverter;

        if (!this.currentObject.MappingSpecification || this.currentObject.MappingSpecification.trim() === '') {
            this.currentObject.MappingSpecification = null;
        }

        if (this.isEditMode) {
            this.documentIntelligenceService.updateModel(this.currentObject.Id, this.currentObject, () => {
                this.handleSubmit(form);
            });
        } else {
            this.documentIntelligenceService.createModel(this.currentObject, () => {
                this.handleSubmit(form);
            });
        }
    }

    closeModal(): void {
        this.activeModal.close();
    }
}
